import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { Col } from 'reactstrap';
import Select from 'react-select';
import { getCountries } from '../../Services/countries';
import { geocodeByPlaceId, getLatLng } from 'react-google-places-autocomplete';
import { Controller } from 'react-hook-form';

const FormLocationFull = ({ control, setValue, address }) => {
  const { t, i18n } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [latForStreetSearch, setLatForStreetSearch] = useState(null);
  const [lngForStreetSearch, setLngForStreetSearch] = useState(null);

  const countries = getCountries(i18n.language);

  useEffect(() => {
    if (address) {
      let countryOption = countries.find((c) => c.value.toLowerCase() === address.countryCode?.toLowerCase());
      if (!countryOption) {
        countryOption = countries.find((c) => c.value.toLowerCase() === address.selectedCountry?.value?.toLowerCase());
      }

      setSelectedCountry(countryOption);
      setSelectedCity(address.city);
      setSelectedStreet(address.street);

      setLatForStreetSearch(address.latForStreetSearch);
      setLngForStreetSearch(address.lngForStreetSearch);

      // Don't remove this below! location is used on BE, even dough there is no field on FE
      const timeoutId = setTimeout(() => {
        setValue('location', address, { shouldDirty: true, shouldValidate: true });
      }, 400); // Adjust delay as necessary to test behavior

      return () => clearTimeout(timeoutId);
    }
  }, [address, setValue]);

  const onSelectCountryChanged = (country) => {
    setSelectedCountry(country);

    setValue('countryControllerSelect', country, { shouldValidate: true });
    setValue('cityControllerSelect', null, { shouldValidate: true });
    setValue('streetControllerSelect', null, { shouldValidate: true });

    setSelectedCity(null);
    setSelectedStreet(null);
    setLatForStreetSearch(null);
    setLngForStreetSearch(null);
  };

  const onSelectCityChanged = (city) => {
    const placeId = city.value.place_id;

    geocodeByPlaceId(placeId).then((results) => {
      getLatLng(results[0])
        .then(({ lat, lng }) => {
          setSelectedCity(city);
          setLatForStreetSearch(lat);
          setLngForStreetSearch(lng);

          setSelectedStreet(null);

          setValue('cityControllerSelect', city, { shouldValidate: true });
          setValue('streetControllerSelect', null, { shouldValidate: true });

          setValue('location', { ...city, latForStreetSearch: lat, lngForStreetSearch: lng });
        })
        .catch((error) => console.error(error));
    });
  };

  const onSelectStreetChanged = (street) => {
    const placeId = street.value.place_id;

    geocodeByPlaceId(placeId)
      .then((results) => getLatLng(results[0]))
      .then(({ lat, lng }) => {
        setValue('location', { street, lat, lng, googlePlaceId: placeId });
      })
      .catch((error) => console.error(error));

    setValue('streetControllerSelect', street, { shouldValidate: true });
    setSelectedStreet(street);
  };

  const composeLocation = () => {
    return (
      <>
        <Col lg="4">
          <h4>
            <span className="text-danger">* </span>
            <b>{t('location.SELECT_COUNTRY')}</b>
          </h4>
          <Controller
            name="countryControllerSelect"
            control={control}
            rules={{ required: t('common.REQUIRED') }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  {...field}
                  // value={selectedCountry}
                  onChange={(val) => {
                    field.onChange(val); // Notify react-hook-form Controller about the change1
                    onSelectCountryChanged(val);
                  }}
                  options={countries}
                  placeholder={t('location.SELECT_COUNTRY')}
                />
                {fieldState.invalid && <div className="text-danger">{fieldState.error?.message}</div>}
              </>
            )}
          />
        </Col>
        <Col lg="4">
          <h4>
            <span className="text-danger">* </span>
            <b>{t('location.SELECT_CITY')}</b>
          </h4>
          <Controller
            name="cityControllerSelect"
            control={control}
            rules={{ required: t('common.REQUIRED') }}
            render={({ field, fieldState }) => (
              <>
                <GooglePlacesAutocomplete
                  selectProps={{
                    isDisabled: !selectedCountry,
                    onChange: (item) => {
                      field.onChange(item);
                      onSelectCityChanged(item);
                    },
                    placeholder: t('common.TYPE_TO_SEARCH'),
                    value: field.value,
                  }}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: [selectedCountry && selectedCountry.value],
                    },
                  }}
                />
                {!selectedCity && <div className="text-danger">{fieldState.error?.message}</div>}
              </>
            )}
          />
        </Col>
        <Col lg="4">
          <h4>
            <span className="text-danger">* </span>
            <b>{t('location.SELECT_STREET')}</b>
          </h4>
          <Controller
            name="streetControllerSelect"
            control={control}
            rules={{ required: t('common.REQUIRED') }}
            render={({ field, fieldState }) => (
              <>
                <GooglePlacesAutocomplete
                  selectProps={{
                    value: field.value,
                    isDisabled: !selectedCity,
                    onChange: (item) => {
                      field.onChange(item);
                      onSelectStreetChanged(item);
                    },
                    placeholder: t('common.TYPE_TO_SEARCH'),
                  }}
                  autocompletionRequest={{
                    location: { lat: latForStreetSearch, lng: lngForStreetSearch },
                    radius: 50000,
                    types: ['address'],
                    fields: ['address_component'],
                  }}
                />
                {!selectedStreet && <div className="text-danger">{fieldState.error?.message}</div>}
              </>
            )}
          />
        </Col>
      </>
    );
  };

  return composeLocation();
};

export default FormLocationFull;
